import { useCallback } from "react";
import React from "react";

const CHILDREN_PER_ROW = 5;

// folders first, then files A–Z
const sortLevel = (arr: any[]) => {
  if (!Array.isArray(arr)) {
    return [];
  }
  return [...arr].sort((a, b) => (a.type !== b.type ? (a.type === "tree" ? -1 : 1) : a.name.localeCompare(b.name)));
};

/**
 * Generate a consistent color from a string using a simple hash function
 * This ensures the same parent always gets the same color for its edges
 */
const stringToColor = (str: string): string => {
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32bit integer
  }

  // Define safe hue ranges (avoiding red and green)
  // Safe ranges:
  // - Orange/Yellow: 20-90° (avoid red at 0°)
  // - Cyan/Blue: 160-250° (avoid green at 120°)
  // - Purple/Magenta: 260-340° (avoid red at 360°)
  const totalSafeDegrees = 240;

  // Map hash to a position within safe ranges
  const position = Math.abs(hash % totalSafeDegrees);

  let hue = 0;
  if (position < 70) {
    // Orange to Yellow range
    hue = 20 + position;
  } else if (position < 160) {
    // Cyan to Blue range
    hue = 160 + (position - 70);
  } else {
    // Purple to Magenta range
    hue = 260 + (position - 160);
  }

  // Saturation: 65-85% (vibrant but not overwhelming)
  const saturation = 65 + (Math.abs(hash >> 8) % 21);

  // Lightness: 45-60% (readable on both light and dark backgrounds)
  const lightness = 45 + (Math.abs(hash >> 16) % 16);

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export const useTreeConversion = () => {
  // Cache for edge colors to avoid redundant hash calculations
  const colorCacheRef = React.useRef<Map<string, string>>(new Map());

  const convertTreeToFlow = useCallback((
    treeData: any,
    expandedDirectories: Record<string, number> = {},
    collapsedDirectories: Record<string, boolean> = {}
  ) => {
    const nodes: any[] = [];
    const edges: any[] = [];

    if (!treeData || !treeData.tree) {
      return { nodes, edges };
    }

    // Clear and reuse cache for this conversion
    const colorCache = colorCacheRef.current;
    colorCache.clear();

    // Helper to get cached color for a parent path
    const getCachedColor = (key: string): string => {
      if (!colorCache.has(key)) {
        colorCache.set(key, stringToColor(key));
      }
      return colorCache.get(key)!;
    };

    const nodesWithIncoming = new Set<string>();
    const nodesWithOutgoing = new Set<string>();
    const visibleNodeIds = new Set<string>();
    const nodeToParent = new Map<string, string | null>();

    const getId = (item: any) =>
      item.path || (item.sha ? `${item.sha}:${item.name}` : `node-${Math.random()}`);

    const walk = (items: any[], parentId: string | null = null, parentPath: string = "", depth = 0) => {
      const level = sortLevel(items);
      const totalChildren = level.length;

      // Check if parent is collapsed - if so, don't show any children
      const isParentCollapsed = parentId ? collapsedDirectories[parentId] : false;

      // Always show all children for fully expanded demo (ignore rowCount limits)
      const visibleCount = isParentCollapsed ? 0 : totalChildren;
      const visibleChildren = level.slice(0, visibleCount);
      const hasMoreChildren = totalChildren > visibleCount && !isParentCollapsed;

      for (const item of visibleChildren) {
        const id = getId(item);
        const isDirectory = item.type === "tree";
        const isFile = item.type === "blob";

        // Track node visibility and parent relationship
        visibleNodeIds.add(id);
        nodeToParent.set(id, parentId);

        nodes.push({
          id,
          type: isDirectory ? "directoryNode" : "fileNode",
          data: {
            label: item.name,
            filePath: item.path,
            type: item.type,
            size: item.size || 0,
            sha: item.sha,
            url: item.url,
            isDirectory,
            isFile,
            width: 240, // Same width for both directories and files
            height: 72, // Same height for both directories and files (36px + 36px)
            depth, // handy if you want to style nodes by depth later
            hasIncomingConnection: false,
            hasOutgoingConnection: false,
            // Store children info for showing "+" button
            totalChildren: item.children?.length || 0,
            visibleChildren: 0, // will be calculated after walk
            hasMoreChildren: false, // will be set after walk
          },
          position: { x: 0, y: 0 }, // Layout will position
        });

        if (parentId) {
          // Generate color based on parent's path/ID for consistent coloring
          // All children of the same parent will have the same edge color
          const edgeColor = getCachedColor(parentPath || parentId);

          edges.push({
            id: `edge-${parentId}__${id}`,
            source: parentId,
            target: id,
            type: "smoothstep",
            style: { stroke: edgeColor, strokeWidth: 2.5 },
          });
          nodesWithOutgoing.add(parentId);
          nodesWithIncoming.add(id);
        }

        if (item.children?.length) {
          walk(item.children, id, item.path || item.name, depth + 1);
        }
      }

      // Update parent node with children visibility info
      if (parentId && hasMoreChildren) {
        const parentNode = nodes.find((n) => n.id === parentId);
        if (parentNode) {
          parentNode.data.visibleChildren = visibleCount;
          parentNode.data.hasMoreChildren = hasMoreChildren;
        }
      }
    };

    // Fix: treeData.tree is an object with children array, not an array itself
    // Pass treeData.tree.children if it exists, otherwise pass treeData.tree (for compatibility)
    const rootItems = Array.isArray(treeData.tree) ? treeData.tree : (treeData.tree?.children || []);
    walk(rootItems, null, "", 0);

    // Update connection flags
    for (const n of nodes) {
      n.data.hasIncomingConnection = nodesWithIncoming.has(n.id);
      n.data.hasOutgoingConnection = nodesWithOutgoing.has(n.id);
    }

    return { nodes, edges };
  }, []);

  return { convertTreeToFlow };
};

